import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserDetails } from "../contexts/UserRegistrationContext";
import { _axios } from "../utils/axios";

export const level2UserVerificationKeyStore = {
  counts: (...args: (number | string | Record<string, unknown> | Array<Number>)[]) => [
    "level-2-user-approvals-counts",
    ...args,
  ],
  list: (...args: (number | string | Record<string, unknown> | Array<Number>)[]) => [
    "level-2-user-approvals",
    ...args,
  ],
  comments: (userId?: string | number) => ["level-2-comments", userId],
};

export function usePendingLevel2UserVerificationCount({
  search,
  filters
}: {
  search?: string;
  filters?: Array<Number>
}) {
  return useQuery({
    queryKey: level2UserVerificationKeyStore.counts(search || "", filters || []),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: number;
      }>("/level-2-user-verification/counts", { search, filters}),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export type UserDetailsUpdated = Omit<UserDetails, "user_id"> & {
  userId: number;
};

export function usePendingLevel2UserVerifications({
  page,
  limit,
  search,
  sort,
  sortVal,
  filters
}: {
  page: number;
  limit: number;
  search?: string;
  sort: string;
  sortVal: number;
  filters?: Array<Number>
}) {
  return useQuery({
    queryKey: level2UserVerificationKeyStore.list(
      page,
      limit,
      sort,
      sortVal,
      search || "",
      filters || []
    ),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: UserDetailsUpdated[];
      }>("/level-2-user-verification", {
        sort,
        sortVal,
        search,
        page,
        limit,
        filters
      }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export type TUserReviewerComment = {
  tenant_id: string;
  id: number;
  user_id: number;
  comment: string;
  comments_by: string;
  status: string;
  updated_date: string;
};

export function useUserVerificationComments({ userId }: { userId?: number }) {
  console.log(typeof userId === "number" && !Number.isNaN(userId));
  return useQuery({
    enabled: typeof userId === "number" && !Number.isNaN(userId),
    queryKey: level2UserVerificationKeyStore.comments(userId),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: TUserReviewerComment[];
      }>("/user-verification-comments/admin", { user_id: userId }),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useUpdateUserVerificationStatusForLevelTwo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      comment: string;
      userId: number;
      email: string;
      name: string;
      action: "approve" | "reject";
    }) => _axios.patch("/level-2-user-verification/action", data),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({
        queryKey: level2UserVerificationKeyStore.comments(userId),
      });
      qc.invalidateQueries({ queryKey: level2UserVerificationKeyStore.list() });
    },
  });
}
